import React, { useState, useRef, useEffect, useCallback } from 'react';
import './Carousel.css';

const Carousel = ({ children, onActiveChange }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [carouselOffset, setCarouselOffset] = useState(0);
  const prevActiveIndexRef = useRef(0);
  const totalItems = React.Children.count(children);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handleSetActive = useCallback((index) => {
    const prevIndex = activeIndex;
    prevActiveIndexRef.current = prevIndex;
    
    // Calculate how much to slide the carousel
    let movement = index - prevIndex;
    
    // Choose shortest path for infinite scrolling
    if (movement > totalItems / 2) {
      movement -= totalItems;
    } else if (movement < -totalItems / 2) {
      movement += totalItems;
    }
    
    setCarouselOffset(prev => prev - movement * 80);
    setActiveIndex(index);
    
    if (onActiveChange) {
      onActiveChange(index);
    }
  }, [activeIndex, totalItems, onActiveChange]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        const newIndex = (activeIndex - 1 + totalItems) % totalItems;
        handleSetActive(newIndex);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        const newIndex = (activeIndex + 1) % totalItems;
        handleSetActive(newIndex);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeIndex, totalItems, handleSetActive]);

  // Minimum swipe distance (in pixels)
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      const newIndex = (activeIndex + 1) % totalItems;
      handleSetActive(newIndex);
    } else if (isRightSwipe) {
      const newIndex = (activeIndex - 1 + totalItems) % totalItems;
      handleSetActive(newIndex);
    }
  };

  // Dynamically render only the tiles needed for current view
  const renderTiles = () => {
    const tiles = [];
    const childrenArray = React.Children.toArray(children);
    
    // Calculate the range of positions we need to render
    // We need tiles from about -240% to +320% to cover visible area plus off-screen tiles
    const minPosition = -240;
    const maxPosition = 320;
    
    // Calculate which tile copies we need for this range
    const tileWidth = 80;
    const carouselWidth = totalItems * tileWidth;
    
    childrenArray.forEach((child, index) => {
      // Calculate this tile's base position without offset
      const basePosition = index * tileWidth;
      
      // Find all copies of this tile that fall within our render range
      let copyIndex = Math.floor((minPosition - basePosition - carouselOffset) / carouselWidth);
      
      while (true) {
        const copyPosition = basePosition + (copyIndex * carouselWidth);
        const finalPosition = copyPosition + carouselOffset;
        
        // Stop if we've gone beyond our render range
        if (finalPosition > maxPosition) break;
        
        // Render this copy if it's in our range
        if (finalPosition >= minPosition) {
          tiles.push(
            <div
              key={`tile-${index}-copy-${copyIndex}`}
              className={`carousel-item ${index === activeIndex ? 'active' : ''}`}
              style={{
                left: `${finalPosition}%`
              }}
            >
              {React.cloneElement(child, {
                children: React.cloneElement(child.props.children, {
                  className: `${child.props.children.props.className || ''} ${index === activeIndex ? '' : 'inactive'}`.trim(),
                  onClick: () => handleSetActive(index)
                })
              })}
            </div>
          );
        }
        
        copyIndex++;
      }
    });
    
    return tiles;
  };

  return (
    <div 
      className="carousel-container"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="carousel-track">
        {renderTiles()}
      </div>
    </div>
  );
};

export default Carousel; 